import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import api from '../api';

function elapsed(startedAt) {
  const secs = Math.floor((Date.now() - new Date(startedAt)) / 1000);
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`
    : `${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
}

function TimerDisplay({ startedAt }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);
  return <div className="timer">{elapsed(startedAt)}</div>;
}

// Group flat op list into jobs → assemblies → ops
function groupOps(operations) {
  const jobMap = new Map();
  for (const op of operations) {
    if (!jobMap.has(op.job_number)) {
      jobMap.set(op.job_number, { job_number: op.job_number, job_title: op.job_title, assemblies: new Map() });
    }
    const job = jobMap.get(op.job_number);
    const asmKey = op.assembly_name;
    if (!job.assemblies.has(asmKey)) {
      job.assemblies.set(asmKey, { name: op.assembly_name, part_number: op.part_number, ops: [] });
    }
    job.assemblies.get(asmKey).ops.push(op);
  }
  return Array.from(jobMap.values()).map(j => ({
    ...j, assemblies: Array.from(j.assemblies.values())
  }));
}

export default function WorkerPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const scannedOpId = searchParams.get('op');
  const scannedRef = useRef(null);
  const [operations, setOperations] = useState([]);
  const [activeLog, setActiveLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [notes, setNotes] = useState('');
  const [showNotes, setShowNotes] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [msg, setMsg] = useState('');

  // Collapsed state — sets of keys
  const [collapsedJobs, setCollapsedJobs] = useState(new Set());
  const [collapsedAsms, setCollapsedAsms] = useState(new Set());

  const flash = (text) => { setMsg(text); setTimeout(() => setMsg(''), 3000); };

  const load = useCallback(async () => {
    try {
      const [opsRes, activeRes] = await Promise.all([
        api.get('/operations/my'),
        api.get('/time-logs/active'),
      ]);
      setOperations(opsRes.data);
      setActiveLog(activeRes.data[0] || null);
    } catch (e) {
      flash('Failed to load — check connection');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Default to collapsed, but expand the job+assembly containing the scanned op
  useEffect(() => {
    if (operations.length === 0) return;
    const scannedOp = scannedOpId ? operations.find(o => String(o.id) === String(scannedOpId)) : null;

    const jobKeys = new Set(operations.map(o => o.job_number));
    const asmKeys = new Set(operations.map(o => `${o.job_number}::${o.assembly_name}`));

    if (scannedOp) {
      jobKeys.delete(scannedOp.job_number);
      asmKeys.delete(`${scannedOp.job_number}::${scannedOp.assembly_name}`);
    }

    setCollapsedJobs(jobKeys);
    setCollapsedAsms(asmKeys);
  }, [operations, scannedOpId]);

  // Scroll to scanned op once loaded
  useEffect(() => {
    if (!loading && scannedOpId && scannedRef.current) {
      scannedRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [loading, scannedOpId]);

  const toggleJob = (jobNum) => setCollapsedJobs(s => {
    const n = new Set(s);
    n.has(jobNum) ? n.delete(jobNum) : n.add(jobNum);
    return n;
  });

  const toggleAsm = (key) => setCollapsedAsms(s => {
    const n = new Set(s);
    n.has(key) ? n.delete(key) : n.add(key);
    return n;
  });

  const startOp = async (operationId) => {
    setActionLoading(true);
    try {
      const { data } = await api.post('/time-logs/start', { operation_id: operationId });
      setActiveLog(data);
      flash('Timer started');
      load();
    } catch (e) {
      flash(e.response?.data?.error || 'Could not start timer');
    } finally { setActionLoading(false); }
  };

  const stopOp = async () => {
    if (!activeLog) return;
    setActionLoading(true);
    try {
      let photoUrl = null;
      if (photo) {
        const fd = new FormData();
        fd.append('photo', photo);
        if (activeOp) {
          fd.append('job_number', activeOp.job_number || '');
          fd.append('assembly_name', activeOp.assembly_name || '');
          fd.append('op_code', activeOp.op_code || '');
          fd.append('op_name', activeOp.name || '');
        }
        const { data } = await api.post('/time-logs/upload-photo', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        photoUrl = data.url;
      }
      await api.post('/time-logs/stop', { log_id: activeLog.id, notes, photo_url: photoUrl });
      setActiveLog(null); setNotes(''); setShowNotes(false); setPhoto(null); setPhotoPreview(null);
      flash('Time logged ✓');
      load();
    } catch (e) {
      flash(e.response?.data?.error || 'Could not stop timer');
    } finally { setActionLoading(false); }
  };

  if (loading) return <div className="loading-screen">Loading your work queue…</div>;

  const activeOp = operations.find(o => o.id === activeLog?.operation_id);
  const queueOps = operations.filter(o => o.id !== activeLog?.operation_id);
  const grouped = groupOps(queueOps);

  return (
    <div className="page">
      <div className="topbar">
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <img src="/logo.jpg" alt="Logo" style={{ height:32, objectFit:'contain' }} />
          <h1>My Work</h1>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ fontSize:13, color:'var(--gray3)' }}>{user.name}</span>
          <button className="btn btn-ghost" style={{ padding:'6px 12px', fontSize:13 }} onClick={() => navigate('/timesheet')}>
            My Timesheet
          </button>
          <button className="btn btn-ghost" style={{ padding:'6px 12px', fontSize:13 }} onClick={logout}>
            Sign out
          </button>
        </div>
      </div>

      <div className="content">
        {msg && (
          <div style={{
            background:'var(--green-lt)', color:'var(--green)',
            padding:'10px 14px', borderRadius:8, marginBottom:12,
            fontWeight:600, fontSize:14,
          }}>{msg}</div>
        )}

        {/* Active timer card */}
        {activeLog && (
          <div className="card" style={{ marginBottom:16, borderLeft:'4px solid var(--blue)' }}>
            <div style={{ fontSize:13, color:'var(--gray3)', marginBottom:2 }}>Currently working on</div>
            <div style={{ fontWeight:700, fontSize:17, marginBottom:2 }}>{activeOp?.name || 'Operation'}</div>
            <div style={{ fontSize:13, color:'var(--gray3)', marginBottom:8 }}>
              {activeOp?.assembly_name} · {activeOp?.job_number}
            </div>
            <TimerDisplay startedAt={activeLog.started_at} />
            {showNotes ? (
              <div style={{ marginTop:10 }}>
                <textarea
                  value={notes} onChange={e => setNotes(e.target.value)}
                  placeholder="Optional notes about this work…" rows={3}
                  style={{ width:'100%', padding:'8px 10px', fontSize:14, border:'1.5px solid var(--gray5)', borderRadius:8, resize:'vertical', marginBottom:8, fontFamily:'inherit' }}
                />
                <div style={{ marginBottom:8 }}>
                  <label style={{ fontSize:13, color:'var(--gray3)', display:'block', marginBottom:4 }}>
                    Attach photo (optional)
                  </label>
                  <input
                    type="file" accept="image/*" capture="environment"
                    style={{ fontSize:13 }}
                    onChange={e => {
                      const f = e.target.files[0];
                      if (!f) { setPhoto(null); setPhotoPreview(null); return; }
                      setPhoto(f);
                      setPhotoPreview(URL.createObjectURL(f));
                    }}
                  />
                  {photoPreview && (
                    <img src={photoPreview} alt="preview" style={{ marginTop:8, maxWidth:'100%', maxHeight:180, borderRadius:8, objectFit:'contain', border:'1px solid var(--gray5)' }} />
                  )}
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <button className="btn btn-danger" style={{ flex:1 }} onClick={stopOp} disabled={actionLoading}>
                    {actionLoading ? 'Saving…' : 'Stop & Save'}
                  </button>
                  <button className="btn btn-ghost" onClick={() => { setShowNotes(false); setPhoto(null); setPhotoPreview(null); }}>Cancel</button>
                </div>
              </div>
            ) : (
              <div style={{ display:'flex', gap:8, marginTop:4 }}>
                <button className="btn btn-danger" style={{ flex:1 }} onClick={() => setShowNotes(true)} disabled={actionLoading}>
                  Stop Timer
                </button>
              </div>
            )}
          </div>
        )}

        {/* Work queue */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
          <div className="section-title" style={{ marginBottom:0 }}>
            {activeLog ? 'Other operations' : 'Your work queue'}
          </div>
          {grouped.length > 0 && (
            <button
              className="btn btn-ghost"
              style={{ padding:'4px 12px', fontSize:13 }}
              onClick={() => {
                const allCollapsed = grouped.every(j => collapsedJobs.has(j.job_number));
                if (allCollapsed) {
                  setCollapsedJobs(new Set());
                  setCollapsedAsms(new Set());
                } else {
                  setCollapsedJobs(new Set(grouped.map(j => j.job_number)));
                  setCollapsedAsms(new Set(grouped.flatMap(j => j.assemblies.map(a => `${j.job_number}::${a.name}`))));
                }
              }}
            >
              {grouped.every(j => collapsedJobs.has(j.job_number)) ? 'Expand all' : 'Collapse all'}
            </button>
          )}
        </div>

        {grouped.length === 0 && (
          <div className="card" style={{ textAlign:'center', color:'var(--gray3)', padding:32 }}>
            No operations assigned right now.<br />Check back later or ask your manager.
          </div>
        )}

        {grouped.map(job => {
          const jobCollapsed = collapsedJobs.has(job.job_number);
          const pendingCount = job.assemblies.reduce((s, a) => s + a.ops.filter(o => o.status !== 'complete').length, 0);

          return (
            <div key={job.job_number} style={{ marginBottom:12 }}>
              {/* Job header */}
              <button
                onClick={() => toggleJob(job.job_number)}
                style={{
                  width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between',
                  padding:'10px 14px', background:'var(--gray1)', color:'#fff',
                  border:'none', borderRadius: jobCollapsed ? 8 : '8px 8px 0 0',
                  cursor:'pointer', fontFamily:'inherit',
                }}
              >
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <span style={{ fontSize:15, fontWeight:700 }}>{job.job_number}</span>
                  <span style={{ fontSize:13, color:'var(--gray4)' }}>{job.job_title}</span>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ fontSize:12, color:'var(--gray4)' }}>{pendingCount} op{pendingCount !== 1 ? 's' : ''}</span>
                  <span style={{ fontSize:16, lineHeight:1 }}>{jobCollapsed ? '▶' : '▼'}</span>
                </div>
              </button>

              {!jobCollapsed && (
                <div style={{ border:'1px solid var(--gray5)', borderTop:'none', borderRadius:'0 0 8px 8px', overflow:'hidden' }}>
                  {job.assemblies.map((asm, asmIdx) => {
                    const asmKey = `${job.job_number}::${asm.name}`;
                    const asmCollapsed = collapsedAsms.has(asmKey);
                    const isLast = asmIdx === job.assemblies.length - 1;

                    return (
                      <div key={asmKey} style={{ borderBottom: isLast ? 'none' : '1px solid var(--gray5)' }}>
                        {/* Assembly header */}
                        <button
                          onClick={() => toggleAsm(asmKey)}
                          style={{
                            width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between',
                            padding:'8px 14px', background:'var(--gray6)',
                            border:'none', borderBottom: asmCollapsed ? 'none' : '1px solid var(--gray5)',
                            cursor:'pointer', fontFamily:'inherit',
                          }}
                        >
                          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            <span style={{ fontSize:13, fontWeight:600, color:'var(--gray2)' }}>{asm.name}</span>
                            {asm.part_number && <span style={{ fontSize:12, color:'var(--gray4)' }}>{asm.part_number}</span>}
                          </div>
                          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            <span style={{ fontSize:12, color:'var(--gray4)' }}>{asm.ops.length} op{asm.ops.length !== 1 ? 's' : ''}</span>
                            <span style={{ fontSize:13, color:'var(--gray4)' }}>{asmCollapsed ? '▶' : '▼'}</span>
                          </div>
                        </button>

                        {/* Operations */}
                        {!asmCollapsed && (
                          <div style={{ padding:'8px 10px', background:'#fff' }}>
                            {asm.ops.map(op => {
                              const isScanned = op.id === scannedOpId;
                              return (
                                <div
                                  key={op.id}
                                  ref={isScanned ? scannedRef : null}
                                  className={`op-card${isScanned ? ' active' : ''}`}
                                  style={isScanned ? { borderColor:'var(--green)', background:'var(--green-lt)' } : {}}
                                >
                                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:4 }}>
                                    <h3>
                                      {op.op_code && <span style={{ fontFamily:'monospace', fontSize:12, color:'var(--blue)', fontWeight:700, marginRight:6 }}>{op.op_code}</span>}
                                      {op.name}
                                    </h3>
                                    <span className={`badge badge-${op.status}`}>{op.status.replace('_',' ')}</span>
                                  </div>
                                  {op.description && (
                                    <div style={{ fontSize:14, color:'var(--gray2)', marginBottom:8 }}>{op.description}</div>
                                  )}
                                  {op.estimated_minutes && (
                                    <div style={{ fontSize:13, color:'var(--gray3)', marginBottom:8 }}>Est. {op.estimated_minutes} min</div>
                                  )}
                                  {!activeLog && (
                                    <button className="btn btn-primary btn-lg" onClick={() => startOp(op.id)} disabled={actionLoading}>
                                      Start Timer
                                    </button>
                                  )}
                                  {activeLog && (
                                    <div style={{ fontSize:13, color:'var(--gray4)', fontStyle:'italic' }}>
                                      Stop current timer first
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
